import React, { useCallback, useEffect, useState } from "react";
import { Button, Image, Text, Subtitle1, Body1, mergeClasses } from "@fluentui/react-components";
import { useNavigate } from "react-router-dom";
import { CafeListItemViewModel } from "@dining/api-client";
import { useStyles } from "./CafeDataStyles";
import {
  abandonCartDialogContent,
  abandonCartDialogTitle,
  appInsightsEvents,
  trackEvent,
  useAppStore,
} from "../../../common";
import { ConfirmationDialog, ConfirmationDialogProps } from "../../shared";
import { HelperUtils } from "../../../common/helperUtils";
import ReactMarkdown from "react-markdown";

interface CafeDataProps {
  cafeData: CafeListItemViewModel;
}

export const CafeData = ({ cafeData }: CafeDataProps) => {
  const styles = useStyles();
  const [dialogProps, setDialogProps] = useState<ConfirmationDialogProps>();
  const [appInsights, cart, context] = useAppStore((state) => [
    state.appInsights,
    state.cart,
    state.context,
  ]);
  const { value: currentCart, abandonCart } = cart;
  useEffect(() => {
    trackEvent(appInsights, appInsightsEvents.Usage.ViewComponent, context, {
      componentName: "CafeData",
      cafeId: cafeData.id,
      cafeName: cafeData.name,
    });
  }, [appInsights, context, cafeData.id, cafeData.name]);
  const navigate = useNavigate();
  const onHoursClick = () => {
    setDialogProps({
      content: (
        <div className={mergeClasses(styles.column, styles.leftSide)}>
          {HelperUtils.getInstance()
            .convertMealPeriodsToStringArray(cafeData.mealPeriods!)
            .map((mealPeriodString: string) => (
              <Body1 key={mealPeriodString}>{mealPeriodString}</Body1>
            ))}
        </div>
      ),
      open: true,
      title: "Hours of Operation",
      showConfirmAction: false,
      showDismissAction: false,
      dismissAction: () => {
        trackEvent(appInsights, appInsightsEvents.Usage.Actions.HoursDialogDismissed, context);
        setDialogProps(undefined);
      },
    });
  };

  const onChangeCafe = useCallback(() => {
    setDialogProps({
      content: abandonCartDialogContent,
      open: true,
      title: abandonCartDialogTitle,
      confirmAction: () => {
        trackEvent(
          appInsights,
          appInsightsEvents.Usage.Actions.AbandonCartDialog.CafeChangeConfirmed,
          context,
          {
            cafeId: currentCart?.cafeId,
            cartId: currentCart?.id,
            cafeName: currentCart?.cafeName,
            component: "CafeData",
          },
        );
        abandonCart();
        setDialogProps(undefined);
        trackEvent(appInsights, appInsightsEvents.Usage.Actions.CafeChanged, context, {});
        navigate("/buildings");
      },
      dismissAction: () => {
        trackEvent(
          appInsights,
          appInsightsEvents.Usage.Actions.AbandonCartDialog.CafeChangeDismissed,
          context,
          {
            cafeId: currentCart?.cafeId,
            cartId: currentCart?.id,
            cafeName: currentCart?.cafeName,
            component: "CafeData",
          },
        );
        setDialogProps(undefined);
      },
    });
  }, [setDialogProps, abandonCart, navigate, appInsights, context, currentCart]);

  const navigateToBuildings = useCallback(() => {
    navigate("/buildings");
    trackEvent(appInsights, appInsightsEvents.Usage.Actions.CafeChanged, context, {});
  }, [context, appInsights, navigate]);
  return (
    <div className={styles.container} data-testid="cafeData">
      {cafeData.imageUrl && (
        <Image
          data-testid="cafeImage"
          src={cafeData.imageUrl}
          onError={(e) => {
            // Retry on failing to load.
            e.currentTarget.src = cafeData.imageUrl || "";
          }}
          alt="Cafe Image"
          fit={"center"}
          className={styles.image}
        />
      )}
      <div className={styles.column}>
        <div className={styles.row}>
          <Subtitle1 data-testid={"cafeName"}>{cafeData.name}</Subtitle1>
          <Button
            appearance="transparent"
            className={mergeClasses(styles.button, styles.rightSide)}
            onClick={currentCart && currentCart.items ? onChangeCafe : navigateToBuildings}
            data-testid="changeCafeLink"
            aria-label="Change cafe location."
            role="link"
          >
            Change
          </Button>
        </div>
        <div className={styles.hourBtn}>
          {/* Hours of Operation Dialog & Trigger Button */}
          {cafeData.mealPeriods && cafeData.mealPeriods.length > 0 && (
            <Button
              appearance="transparent"
              className={mergeClasses(styles.button, styles.leftSide)}
              onClick={onHoursClick}
              aria-label="Tap to view cafe hours."
            >
              Hours
            </Button>
          )}
          {dialogProps && <ConfirmationDialog {...dialogProps} />}
        </div>
      </div>
      {cafeData.mealPeriods && cafeData.mealPeriods.length > 0 && (
        <div className={styles.mealContainer}>
          {cafeData.mealPeriods &&
            cafeData.mealPeriods.map((mealPeriod) => (
              <div key={mealPeriod?.id}>
                <Body1 className={styles.containerMealText}>{mealPeriod?.name}</Body1>
                <Body1 className={styles.containerTimingText}>{mealPeriod?.hoursOfOperation}</Body1>
              </div>
            ))}
        </div>
      )}
      {cafeData.disclaimer && (
        <div className={styles.disclaimerBox}>
          <Text className={styles.disclaimerHeading}>Disclaimer</Text>

          <div className={styles.disclaimerTextContainer}>
            <div className={styles.disclaimerText}>
              <ReactMarkdown>{cafeData.disclaimer}</ReactMarkdown>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
